/**
 * Minimal in-place DOM morph (morphdom-style) for Custom Offer Unit templates.
 *
 * COU renders a publisher HTML string with `{{macro}}` slots. Re-assigning
 * `innerHTML` on every offer change tears down and rebuilds the whole template
 * DOM — losing focus / scroll / media-playback state and re-triggering CSS
 * entrance animations on each step.
 *
 * `morphInnerHTML` parses the freshly-rendered markup and mutates only the
 * attributes, text, and subtrees that differ from the live DOM, preserving the
 * identity (and live state) of everything unchanged. Element identity can be
 * pinned across structural changes (e.g. a conditional block toggling above it)
 * by giving the element an `id`.
 *
 * A container's contents can be opted out of that reconciliation — see
 * `isPreserved`, which is the file's single answer to "is this subtree ours?".
 */
import { isSdkOwnedProgressRegion } from './couProgress';
import { COU_PRESERVE_ATTRIBUTE } from './macroAllowlist';
import { rehydrateScripts } from './scriptRehydrate';
const ELEMENT_NODE = 1;
/**
 * The one question this file asks about a subtree: is it the template's to
 * reconcile? The element's own attributes are still synced from the template
 * either way; only its contents are left exactly as they are.
 *
 * Two things answer yes, and they are deliberately not the same test.
 *
 * `data-ms-preserve` — the publisher's own marker, for third-party embeds. A
 * `<script>` elsewhere in a COU template (see `scriptRehydrate`) runs once and
 * paints into this container, and that output is not part of the template
 * markup — so every later morph would reconcile it away as a stale node,
 * permanently, because the script does not run a second time. Marking the
 * container hands its inside to whatever wrote it, while the template stays in
 * charge of the container itself. It means that to `rehydrateScripts` too: a
 * `<script>` written inside a preserved container by the template never runs,
 * and one the page appends there is never re-run. So a third-party loader has
 * to be created from outside the container, the way the decision script in the
 * documented split-test template does, and paint into it from there.
 *
 * `isSdkOwnedProgressRegion` — a `[data-ms-progress]` region the SDK is
 * currently filling (see `couProgress`). Those children are SDK output, not
 * template markup: reconciling N repeated tiles against the template's single
 * unit would delete every repeat past the first on each step. It cannot be
 * folded into the attribute test, for two independent reasons:
 *
 *   - `syncAttributes` runs before the skip is consulted, so any marker the SDK
 *     wrote onto the region is already gone by then. Ownership has to live off
 *     the element, which is why `couProgress` keeps it in a `WeakSet`.
 *   - A bare `[data-ms-progress]` match would over-skip. Only the regions the
 *     SDK actually fills are ours; a region the publisher filled themselves
 *     (`state` mode) is ordinary template markup, and a `{{macro}}` in it must
 *     keep re-resolving on every offer step. Ownership is per region and per
 *     render, so it distinguishes the two; an attribute on the tag cannot.
 *
 * `rehydrateScripts` deliberately does not consult the second arm. Its skip is
 * about nodes the SDK never created and so cannot identify; an inert `<script>`
 * inside a repeated tile *is* SDK-written template markup, and running it once
 * per copy is the same rule as everywhere else in the template.
 */
function isPreserved(node) {
    if (node.nodeType !== ELEMENT_NODE) {
        return false;
    }
    return (node.hasAttribute(COU_PRESERVE_ATTRIBUTE) ||
        isSdkOwnedProgressRegion(node));
}
function getNodeKey(node) {
    if (node.nodeType === ELEMENT_NODE) {
        return node.getAttribute('id') ?? '';
    }
    return '';
}
/** Same node "shape" (incl. `id`) — safe to morph in place rather than replace. */
function compatible(a, b) {
    if (a.nodeType !== b.nodeType) {
        return false;
    }
    if (a.nodeType === ELEMENT_NODE) {
        return (a.localName === b.localName &&
            a.namespaceURI === b.namespaceURI &&
            getNodeKey(a) === getNodeKey(b));
    }
    return true;
}
function indexKeyedNodes(root, map) {
    let node = root.firstChild;
    while (node) {
        if (node.nodeType === ELEMENT_NODE) {
            const key = getNodeKey(node);
            if (key) {
                map.set(key, node);
            }
            /**
             * The preserved element itself is indexed like any other, so it can still
             * be matched and moved into position. Its contents are not: a key match
             * elsewhere in the new markup would otherwise move a node out of it, and
             * a key with no match would strand in `keyed` for the final sweep to
             * delete — markup no morph pass in this file ever claims.
             */
            if (!isPreserved(node)) {
                indexKeyedNodes(node, map);
            }
        }
        node = node.nextSibling;
    }
}
function syncAttributes(fromEl, toEl) {
    const toAttrs = toEl.attributes;
    for (let i = 0; i < toAttrs.length; i++) {
        const attr = toAttrs.item(i);
        if (attr && fromEl.getAttribute(attr.name) !== attr.value) {
            fromEl.setAttribute(attr.name, attr.value);
        }
    }
    const fromAttrs = fromEl.attributes;
    for (let i = fromAttrs.length - 1; i >= 0; i--) {
        const attr = fromAttrs.item(i);
        if (attr && !toEl.hasAttribute(attr.name)) {
            fromEl.removeAttribute(attr.name);
        }
    }
}
/**
 * Forget the keys held by nodes inside `parent`, so the end-of-morph sweep of
 * unmatched keyed nodes cannot reach into a preserved subtree. Needed for the
 * render on which the template first adds `data-ms-preserve`: the container was
 * still unpreserved when `indexKeyedNodes` walked it.
 */
function releaseKeysUnder(parent, keyed) {
    let node = parent.firstChild;
    while (node) {
        if (node.nodeType === ELEMENT_NODE) {
            const key = getNodeKey(node);
            if (key && keyed.get(key) === node) {
                keyed.delete(key);
            }
            releaseKeysUnder(node, keyed);
        }
        node = node.nextSibling;
    }
}
function morphNode(fromNode, toNode, keyed) {
    if (fromNode.nodeType === ELEMENT_NODE) {
        const wasPreserved = isPreserved(fromNode);
        syncAttributes(fromNode, toNode);
        /**
         * Read again after the attribute sync, so adding or dropping
         * `data-ms-preserve` in the template takes effect on the render that
         * changes it. A container already preserved when `indexKeyedNodes` ran has
         * nothing of its own in `keyed`, so only the render that adds the attribute
         * pays for the release walk. The ownership arm cannot flip here — it is
         * only ever changed between morphs, by `couProgress` — so it never does.
         *
         * Attributes still sync on a skipped region; the SDK rewrites the ones it
         * owns afterwards, the way `applyOfferNavVisibility` does.
         */
        if (isPreserved(fromNode)) {
            if (!wasPreserved) {
                releaseKeysUnder(fromNode, keyed);
            }
            return;
        }
        morphChildren(fromNode, toNode, keyed);
        return;
    }
    if (fromNode.nodeValue !== toNode.nodeValue) {
        fromNode.nodeValue = toNode.nodeValue;
    }
}
function morphChildren(fromParent, toParent, keyed) {
    let curTo = toParent.firstChild;
    let curFrom = fromParent.firstChild;
    while (curTo) {
        const toNext = curTo.nextSibling;
        const toKey = getNodeKey(curTo);
        // Reuse a keyed node from anywhere, moving it into position — survives
        // reordering and structural shifts around it.
        if (toKey) {
            const match = keyed.get(toKey);
            if (match && compatible(match, curTo)) {
                if (match !== curFrom) {
                    fromParent.insertBefore(match, curFrom);
                }
                else {
                    curFrom = curFrom.nextSibling;
                }
                morphNode(match, curTo, keyed);
                keyed.delete(toKey);
                curTo = toNext;
                continue;
            }
        }
        if (!curFrom) {
            fromParent.insertBefore(curTo, null);
            curTo = toNext;
            continue;
        }
        if (compatible(curFrom, curTo)) {
            const fromNext = curFrom.nextSibling;
            morphNode(curFrom, curTo, keyed);
            curFrom = fromNext;
            curTo = toNext;
            continue;
        }
        // Keyed existing node that doesn't belong here — reserve it for its own key
        // match (or final cleanup); never consume it positionally.
        if (getNodeKey(curFrom)) {
            curFrom = curFrom.nextSibling;
            continue;
        }
        // The existing node still lines up with the next new node → curTo is an
        // insertion; splice it in and keep the existing node for the next round.
        if (toNext && compatible(curFrom, toNext)) {
            fromParent.insertBefore(curTo, curFrom);
            curTo = toNext;
            continue;
        }
        // Otherwise the existing node is stale → drop it and retry curTo.
        const stale = curFrom;
        curFrom = curFrom.nextSibling;
        fromParent.removeChild(stale);
    }
    while (curFrom) {
        const next = curFrom.nextSibling;
        if (!getNodeKey(curFrom)) {
            fromParent.removeChild(curFrom);
        }
        curFrom = next;
    }
}
/**
 * Patch `root`'s children to match `html`, mutating in place. Falls back to a
 * plain `innerHTML` assignment when `root` is empty (first render) — identical
 * result, no diff cost.
 *
 * Both write paths go through the HTML parser, which leaves any `<script>` in
 * the markup inert, so both end in `rehydrateScripts` to run it. That keeps
 * script execution attached to the single place COU markup reaches the DOM:
 * `layout.render` and the template paths in `CustomOfferUnit` both land here.
 */
export function morphInnerHTML(root, html) {
    if (!root.firstChild) {
        root.innerHTML = html;
        rehydrateScripts(root);
        return;
    }
    const template = root.ownerDocument.createElement('template');
    template.innerHTML = html;
    const keyed = new Map();
    indexKeyedNodes(root, keyed);
    morphChildren(root, template.content, keyed);
    // Drop keyed nodes that no longer appear in the new markup.
    keyed.forEach(node => node.parentNode?.removeChild(node));
    rehydrateScripts(root);
}
